// src/modules/meal/components/RecipeSelectorModal.js

import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, Card } from '../../../shared/components/ui';
import { recipeService } from '../services/recipeService';
import { useTranslation } from 'react-i18next';

const RecipeSelectorModal = ({ isOpen, onClose, onSelectRecipe, mealType }) => {
    const { t } = useTranslation();
    const [recipes, setRecipes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterFavorites, setFilterFavorites] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 20,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Cargar recetas con filtros
    const loadRecipes = useCallback(async () => {
        if (!isOpen) return;

        setIsLoading(true);
        setError('');

        try {
            const filters = {
                is_archived: false // Solo recetas no archivadas
            };

            // Aplicar filtro de favoritos
            if (filterFavorites) {
                filters.is_favorite = true;
            }

            const result = await recipeService.getAllRecipes(
                currentPage,
                20,
                searchTerm,
                filters
            );

            if (result.success) {
                setRecipes(result.data.recipes);
                setPagination(result.data.pagination);
            } else {
                setError(t('menu.selector.loadError') || t('common.error'));
            }
        } catch (err) {
            setError(err.message || (t('menu.selector.loadError') || t('common.error')));
        } finally {
            setIsLoading(false);
        }
    }, [isOpen, currentPage, searchTerm, filterFavorites]);

    // Debounce para búsqueda
    useEffect(() => {
        const timer = setTimeout(() => {
            if (isOpen) {
                loadRecipes();
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [loadRecipes, isOpen]);

    // Reset al abrir
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setCurrentPage(1);
            setFilterFavorites(false);
        }
    }, [isOpen]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleSelectRecipe = (recipeId) => {
        onSelectRecipe(recipeId);
        onClose();
    };

    const getMealTypeName = (type) => {
        switch (type) {
            case 'breakfast': return t('meal.types.breakfast');
            case 'lunch': return t('meal.types.lunch');
            case 'dinner': return t('meal.types.dinner');
            default: return type;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="p-4 sm:p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">
                                {t('menu.selector.title')}
                            </h2>
                            {mealType && (
                                <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2 truncate">
                                    {t('menu.selector.for')} <span className="font-semibold">{getMealTypeName(mealType)}</span>
                                </p>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 text-3xl sm:text-2xl font-bold flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center p-2 -m-2"
                            aria-label="Cerrar"
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                    {/* Búsqueda y filtros */}
                    <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
                        {/* Barra de búsqueda */}
                        <div>
                            <Input
                                type="text"
                                placeholder={t('menu.selector.searchPlaceholder')}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="w-full min-h-[44px] text-sm sm:text-base"
                            />
                        </div>

                        {/* Filtros */}
                        <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                                <input
                                    type="checkbox"
                                    checked={filterFavorites}
                                    onChange={(e) => {
                                        setFilterFavorites(e.target.checked);
                                        setCurrentPage(1);
                                    }}
                                    className="w-5 h-5 sm:w-4 sm:h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <span className="text-xs sm:text-sm text-gray-700">
                                    {t('menu.selector.favoritesOnly')}
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Loading */}
                    {isLoading && (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">{t('menu.selector.loading')}</p>
                        </div>
                    )}

                    {/* Lista de recetas */}
                    {!isLoading && recipes.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            <p className="text-lg">{t('menu.selector.noResults')}</p>
                            <p className="text-sm mt-2">{t('menu.selector.noResultsHint')}</p>
                        </div>
                    )}

                    {!isLoading && recipes.length > 0 && (
                        <div className="space-y-2 sm:space-y-3">
                            {recipes.map((recipe) => (
                                <Card key={recipe.id} className="hover:shadow-md transition-shadow">
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 sm:mb-2">
                                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                                                    {recipe.name}
                                                </h3>
                                                {recipe.isFavorite && (
                                                    <span className="text-yellow-500 text-lg sm:text-base flex-shrink-0">⭐</span>
                                                )}
                                            </div>

                                            {/* Ingredientes */}
                                            <div className="text-xs sm:text-sm text-gray-500">
                                                {recipe.ingredients && recipe.ingredients.length > 0 && (
                                                    <span className="line-clamp-2">
                                                        {recipe.ingredients.slice(0, 3).map(ing => ing.item).join(', ')}
                                                        {recipe.ingredients.length > 3 && ` (+${recipe.ingredients.length - 3} más)`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleSelectRecipe(recipe.id)}
                                            className="flex-shrink-0 min-h-[44px] w-full sm:w-auto text-sm sm:text-base"
                                        >
                                            {t('menu.selector.select')}
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Paginación */}
                    {!isLoading && pagination.total_pages > 1 && (
                        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={!pagination.has_prev}
                                className="min-h-[44px] w-full sm:w-auto text-sm sm:text-base"
                            >
                                <span className="hidden sm:inline">← {t('common.previous')}</span><span className="sm:hidden">←</span>
                            </Button>
                            <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                                {t('menu.selector.page')} {pagination.page} {t('common.of') || '/'} {pagination.total_pages}
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                disabled={!pagination.has_next}
                                className="min-h-[44px] w-full sm:w-auto text-sm sm:text-base"
                            >
                                <span className="hidden sm:inline">{t('common.next')} →</span><span className="sm:hidden">→</span>
                            </Button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 sm:p-6 border-t border-gray-200">
                    <div className="flex justify-end">
                        <Button
                            variant="ghost"
                            onClick={onClose}
                            className="min-h-[44px] w-full sm:w-auto text-sm sm:text-base"
                        >
                            {t('common.cancel')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecipeSelectorModal;

